import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { amplitudeContextSelector } from '@/features/amplitude/store/amplitude';
import { WRAPPER_TO_PROVIDER_EVENT_TYPES } from '@/features/event-handlers/wrapper-event-handlers/provider-events/types';
import { Provider } from '@/features/game-context/types';
import { getErrorType } from '@/features/game-window-integration/helpers/error-mapping';
import { handleUnknownEvent } from '@/features/game-window-integration/helpers/generic-helpers';
import {
  EvolutionMessages,
  EvolutionPopupTypes,
  ProviderLoggerActionNames,
  ProviderLoggerMessages,
} from '@/features/game-window-integration/types';
import {
  DdEventCategory,
  DdEventType,
  Logger,
} from '@/features/logging/logger';
import { WrapperProxy } from '@/features/wrapper-proxy/wrapper-proxy';
import { processPayload } from '@/utils/process-error-payload';

export const useEvolutionEventHandlers = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [gameHandler, setGameHandler] = useState<any>();
  const [popupId, setPopupId] = useState<string>();

  const { gameSwitchEnabled } = useSelector(amplitudeContextSelector);

  const handleReady = useCallback((e) => {
    WrapperProxy.setGameLoaded();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handler: any = e.source;
    setGameHandler(e.source);
    if (handler) {
      const messages = [
        {
          command: EvolutionMessages.EVENT_SUBSCRIBE,
          event: EvolutionMessages.GAME_LIFECYCLE,
        },
        {
          command: EvolutionMessages.EVENT_SUBSCRIBE,
          event: EvolutionMessages.VOLUME_STATE_CHANGE,
        },
        {
          command: EvolutionMessages.EVENT_SUBSCRIBE,
          event: EvolutionMessages.SHOW_POPUP,
        },
      ];
      messages.forEach((message) => {
        handler.postMessage(message, '*');
      });
    }
  }, []);

  const handleLifecycle = useCallback((event) => {
    if (typeof event.data?.interruptAllowed !== undefined) {
      WrapperProxy.setSpinInProgress(event.data.interruptAllowed === false);
    }
  }, []);

  const handleVolumeChange = useCallback(
    (event) => WrapperProxy.setAudio(event?.data?.isVolumeOn ?? false),
    [],
  );

  const handleApplicationLoadStarted = useCallback(
    (event) => {
      if (event.data.tableId) {
        Logger.rumAction({
          name: ProviderLoggerActionNames.GAME_SWITCH,
          useCase: DdEventCategory.ProviderIntegration,
          description: ProviderLoggerMessages.GAME_SWITCH,
          eventType: DdEventType.Info,
          context: {
            provider: Provider.EVOLUTION,
            rgsCode: event.data.tableId,
            stringifiedPayload: processPayload(event.data),
          },
        });

        WrapperProxy.setGameSwitch(
          {
            rgsCode: event.data.tableId,
            provider: Provider.EVOLUTION,
          },
          gameSwitchEnabled ?? false,
        );
      }
    },
    [gameSwitchEnabled],
  );

  const handlePopup = useCallback((event) => {
    if (event.data?.popupType === EvolutionPopupTypes.LOW_BALANCE) {
      WrapperProxy.setProviderError({
        display: true,
        errorCode: EvolutionPopupTypes.LOW_BALANCE,
        errorMessage: '',
        errorType: getErrorType(
          EvolutionPopupTypes.LOW_BALANCE,
          Provider.EVOLUTION,
          false,
          event,
        ),
      });
      setPopupId(event.data?.popupId);
    }
  }, []);

  const eventHandlerMapper = useMemo(
    () => ({
      [EvolutionMessages.GAME_LOAD_COMPLETED]: handleReady,
      [EvolutionMessages.GAME_LIFECYCLE]: handleLifecycle,
      [EvolutionMessages.VOLUME_STATE_CHANGE]: handleVolumeChange,
      [EvolutionMessages.SHOW_POPUP]: handlePopup,
      [EvolutionMessages.APPLICATION_LOAD_STARTED]:
        handleApplicationLoadStarted,
    }),
    [
      handleLifecycle,
      handleReady,
      handleVolumeChange,
      handlePopup,
      handleApplicationLoadStarted,
    ],
  );

  const providerEventHandler = useCallback(
    (event) => {
      if (event?.data?.event) {
        if (eventHandlerMapper[event.data.event]) {
          eventHandlerMapper[event.data.event]?.(event);
        } else {
          handleUnknownEvent(event.data);
        }
      }
    },
    [eventHandlerMapper],
  );

  const wrapperEventHandler = useCallback(
    (e: MessageEvent) => {
      const executeEvent = (event) => {
        if (gameHandler) {
          gameHandler.postMessage(event, '*');
        } else {
          Logger.rumError({
            name: 'Failed to get handler for provider',
            useCase: DdEventCategory.ProviderIntegration,
            eventType: DdEventType.Error,
            context: {
              provider: Provider.EVOLUTION,
            },
          });
        }
      };

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_AUDIO) {
        executeEvent({
          command: 'EVO:CONTROL_VOLUME',
          action: e.data.payload ? 'unmute' : 'mute',
        });
        WrapperProxy.setAudio(e.data.payload);
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.RELOAD_BALANCE) {
        executeEvent({
          command: EvolutionMessages.REFRESH_BALANCE,
        });
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.AUTO_SPIN_STOP) {
        executeEvent({
          command: EvolutionMessages.STOP_AUTOPLAY,
        });
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.DISMISS_ERROR) {
        executeEvent({
          command: EvolutionMessages.POPUP_CLOSED,
          popupId,
        });
      }
    },
    [gameHandler, popupId],
  );

  return { wrapperEventHandler, providerEventHandler };
};
